import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ArrowUpIcon, ClockIcon, InfoIcon, SendIcon, TrendIcon, WalletIcon } from "@/assets/icons";
import { PERSONA, QUICK_QUESTIONS } from "@/features/assistant/persona";
import { useChatStore } from "@/store/chat";
import { useProfileStore } from "@/store/profile";
import { useUiStore } from "@/store/ui";
import { useDragScroll } from "@/utils/useDragScroll";
import { DebugPanel } from "./components/DebugPanel";
import cls from "./styles.module.scss";

const QUICK_ICONS = [TrendIcon, WalletIcon, ArrowUpIcon, ClockIcon, InfoIcon];

export const ChatPage = () => {
  const { t } = useTranslation();
  const messages = useChatStore((s) => s.messages);
  const sending = useChatStore((s) => s.sending);
  const start = useChatStore((s) => s.start);
  const send = useChatStore((s) => s.send);
  const personaQueue = useChatStore((s) => s.personaQueue);
  const personaIndex = useChatStore((s) => s.personaIndex);
  const personaDone = useProfileStore((s) => s.personaDone);
  const debug = useUiStore((s) => s.debug);
  const [text, setText] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragScroll = useDragScroll<HTMLDivElement>();

  useEffect(() => {
    start();
  }, [start]);

  useEffect(() => {
    const el = wrapRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const personaStep =
    !personaDone && personaIndex < personaQueue.length
      ? PERSONA[personaQueue[personaIndex]]
      : null;

  const submit = (value: string) => {
    if (!value.trim() || sending) return;
    setText("");
    send(value);
  };

  return (
    <section className={cls.screen}>
      <div className={cls.head}>
        <span className={cls.av}>ИИ</span>
        <div style={{ minWidth: 0 }}>
          <div className={cls.title}>{t("chat.title")}</div>
          <div className={cls.sub}>{t("chat.subtitle")}</div>
        </div>
      </div>

      <div className={cls.wrap} ref={wrapRef}>
        {messages.map((m) => (
          <div
            key={m.id}
            className={classNames(cls.bubble, m.who === "me" ? cls.me : cls.bot, m.failed && cls.failed)}
          >
            {m.pending ? (
              <span className={cls.sk} />
            ) : (
              <>
                <span className={cls.text}>{m.text}</span>
                {debug && m.meta && <DebugPanel meta={m.meta} />}
              </>
            )}
            <span className={cls.tm}>{m.time}</span>
          </div>
        ))}
      </div>

      <div className={cls.dock}>
        <div className={cls.quickrow} {...dragScroll}>
          {personaStep
            ? (personaStep.chips ?? []).map((c) => (
                <button key={c} type="button" className={cls.chip} onClick={() => submit(c)}>
                  <span className={cls.lbl}>{c}</span>
                </button>
              ))
            : QUICK_QUESTIONS.map((q, i) => {
                const Icon = QUICK_ICONS[i];
                return (
                  <button
                    key={q.label}
                    type="button"
                    className={classNames(cls.chip, cls[`q_${q.kind}`])}
                    onClick={() => submit(q.label)}
                  >
                    <span className={cls.qi}>
                      <Icon />
                    </span>
                    <span className={cls.lbl}>{q.label}</span>
                  </button>
                );
              })}
        </div>
        <div className={cls.input}>
          <div className={cls.f}>
            <textarea
              className={cls.inp}
              rows={1}
              value={text}
              placeholder={personaStep ? personaStep.placeholder : t("chat.placeholder")}
              onChange={(e) => {
                setText(e.target.value);
                const el = e.currentTarget;
                el.style.height = "auto";
                el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit(text);
                  e.currentTarget.style.height = "auto";
                }
              }}
            />
          </div>
          <button
            type="button"
            className={cls.send}
            aria-label="Отправить"
            disabled={sending || !text.trim()}
            onClick={() => submit(text)}
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </section>
  );
};
